import api from '@/services/api'

/**
 * Best-effort client error reporter. Captures uncaught Vue/runtime/promise errors and ships them to the
 * backend (POST /ops/client-errors) so an operator-side crash is observable instead of a silent white
 * screen. Deduped per session, size-capped, and ALWAYS swallows its own failures (never loops on a report
 * that itself errors, never throws into the app).
 */
const seen = new Set()

export const reportClientError = (err, context = {}) => {
  try {
    const message = (err && err.message) ? String(err.message) : String(err || 'Unknown error')
    const stack = (err && err.stack) ? String(err.stack) : ''

    // Dedupe identical errors within a session so a render loop can't flood the sink.
    const key = message + '|' + (context.info || context.source || '')
    if (seen.has(key)) return
    seen.add(key)

    const payload = {
      message: message.slice(0, 500),
      stack: stack.slice(0, 8000),
      url: typeof location !== 'undefined' ? location.href : null,
      context,
    }

    // Fire-and-forget; a failed report (offline, 401 on the login screen, throttled) is simply dropped.
    api.post('/ops/client-errors', payload).catch(() => {})
  } catch (_e) {
    // Never let the reporter itself break the app.
  }
}

/** Wire the global capture points. Call once at app bootstrap. */
export const installGlobalErrorReporting = (app) => {
  app.config.errorHandler = (err, _instance, info) => {
    // Keep the console signal for local dev…
    // eslint-disable-next-line no-console
    console.error('[admin] vue error:', err, info)
    reportClientError(err, { source: 'vue', info })
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('error', (e) => reportClientError(e.error || e.message, { source: 'window' }))
    window.addEventListener('unhandledrejection', (e) => reportClientError(e.reason, { source: 'promise' }))
  }
}
